import React from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

/**
 * LE MOUVEMENT DES MODALES DU TABLEAU DE BORD, ÉCRIT UNE SEULE FOIS.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * PORTÉ DEPUIS LE SITE PUBLIC le 29/10/2026 (`Frontend/.../ModaleAnimee.jsx`), avec le MÊME
 * ressort et les mêmes durées. C'est délibéré : quelqu'un qui passe du site au tableau de
 * bord doit retrouver la même sensation, sans savoir dire pourquoi. Deux réglages voisins
 * mais distincts se remarquent ; identiques, ils disparaissent.
 *
 * POURQUOI UN COMPOSANT ET PAS DES PROPS RECOPIÉES. Le dash compte 242 surcouches de modale
 * réparties sur 56 fichiers. Les mêmes réglages collés 242 fois donnent le bon résultat
 * aujourd'hui et une dérive garantie ensuite.
 *
 * LE VOILE ET LE PANNEAU N'ARRIVENT PAS ENSEMBLE. Le fond s'assombrit d'abord, le panneau
 * monte ensuite : l'ordre dit au regard où se poser. Simultanés, ils font un « flash ».
 *
 * LA SORTIE EST PLUS RAPIDE QUE L'ENTRÉE. Une entrée se regarde, une sortie s'attend.
 *
 * ── CE QUI CHANGE PAR RAPPORT À LA VERSION DU SITE ──
 * Les modales du site portent leur voile dans une CLASSE CSS (`.modal-overlay`) ; celles du
 * dash l'écrivent en STYLE EN LIGNE, fichier par fichier. `style` accepte donc ici le voile
 * complet, et `VOILE` plus bas sert de valeur par défaut raisonnable pour les dizaines de
 * modales qui n'ont rien de particulier à dire.
 *
 * ── CE QU'IL FAUT SAVOIR POUR S'EN SERVIR ──
 * LA CONDITION D'OUVERTURE EST UNE PROP, PAS UN `if` AUTOUR. `AnimatePresence` a besoin que
 * l'élément reste monté le temps de sa sortie : un `{ouvert && <ModaleAnimee…>}` démonterait
 * tout avant que l'animation commence, et la sortie ne se jouerait jamais. C'est l'erreur
 * qu'on fait une fois, puis plus jamais.
 *
 * LES ANIMATIONS CSS EXISTANTES DOIVENT PARTIR. Une surcouche qui porte déjà un
 * `animation: fadeIn .2s` la superposerait à celle-ci, et l'ouverture sauterait.
 *
 * `useReducedMotion()` EST RESPECTÉ : pour qui a demandé moins d'animations, il ne reste
 * qu'un fondu court. Ce n'est pas une option, c'est un réglage système.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 */

/* Un ressort plutôt qu'une durée : une fenêtre qui se pose a une inertie, une fenêtre qui
   suit une courbe fixe a l'air de glisser sur un rail. Amorti haut (30) — on veut qu'elle se
   pose, pas qu'elle rebondisse. */
const RESSORT = { type: "spring", stiffness: 300, damping: 30, mass: 0.9 };
const SORTIE  = { duration: 0.14, ease: "easeIn" };
const DOUX    = { duration: 0.15 };

const MOUVEMENTS = {
  panneau: { entree: { opacity: 0, y: 24, scale: 0.96 }, sortie: { opacity: 0, y: 12, scale: 0.98 } },
  plein:   { entree: { opacity: 0, scale: 0.94 },        sortie: { opacity: 0, scale: 0.97 } },
  volet:   { entree: { opacity: 0, y: -16 },             sortie: { opacity: 0, y: -10 } },
};

/**
 * Le voile par défaut du dash.
 *
 * `zIndex: 9999` PARCE QUE LES MODALES DU DASH COHABITENT. Une modale de détail peut en
 * ouvrir une seconde de confirmation ; relevé dans le dépôt, les valeurs vont de 1000 à
 * 10000 selon les fichiers. 9999 passe au-dessus de tout ce qui existe sans dépasser les
 * rares 10000 déjà posés volontairement au-dessus de tout.
 *
 * `overflowY: auto` SUR LE VOILE, ET NON SUR LE PANNEAU : un formulaire long doit faire
 * défiler la page entière de la modale, pas seulement son intérieur — sinon le bouton
 * « Enregistrer » reste hors d'atteinte sur un petit écran.
 */
export const VOILE = {
  position: "fixed", inset: 0, background: "rgba(15,23,42,.55)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "40px 20px", overflowY: "auto", zIndex: 9999,
};

export default function ModaleAnimee({
  ouvert,
  onFermer,
  variante = "panneau",
  layoutId,

  // Le voile. `style` REMPLACE le défaut plutôt que de s'y ajouter : certaines modales
  // veulent un voile centré verticalement, d'autres un fond plus clair — mélanger les deux
  // sources produirait des surprises invisibles à la lecture.
  className,
  style,

  // Le panneau. `sansPanneau` rend les enfants tels quels, pour les modales dont le contenu
  // porte déjà sa propre mise en page.
  panneauClassName,
  panneauStyle,
  sansPanneau = false,

  fermerAuClicExterieur = true,

  /* LE CLIC SUR LE VOILE, REPRIS TEL QUEL — ajouté le 30/10/2026 pour la conversion en
     masse des surcouches écrites à la main.
     Les 231 surcouches du dash ne ferment pas toutes de la même façon : `onClick={onClose}`
     ici, `onClick={e => { if (e.target === e.currentTarget) setX(null) }}` là, et parfois
     rien du tout. Les réécrire en `onFermer` demanderait de deviner l'intention à chaque
     fois — et se tromper rend une fenêtre impossible à fermer, ou la ferme au moindre clic
     dans un formulaire. Quand ce gestionnaire est fourni, il REMPLACE celui de la modale :
     le comportement d'origine est conservé au caractère près, et la conversion ne porte
     plus que sur l'animation. */
  onClicVoile,

  children,
}) {
  const reduit = useReducedMotion();
  const m = MOUVEMENTS[variante] || MOUVEMENTS.panneau;

  /* AVEC `layoutId`, NI `initial` NI `animate` : Framer calcule alors la trajectoire depuis
     l'élément d'origine, et les imposer entrerait en conflit — la modale sauterait avant de
     se replacer. */
  const anime = !layoutId;

  /* ÉCHAP FERME, et l'écouteur n'existe QUE pendant l'ouverture. Posé en permanence, il
     intercepterait la touche sur des écrans où elle ne doit rien faire — et plusieurs
     modales ouvertes en poseraient chacune un, fermant tout d'un coup. */
  React.useEffect(() => {
    if (!ouvert || !onFermer) return;
    const surTouche = (e) => { if (e.key === "Escape") onFermer(); };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [ouvert, onFermer]);

  return (
    <AnimatePresence>
      {ouvert && (
        <motion.div
          className={className}
          style={style || VOILE}
          onClick={onClicVoile || (fermerAuClicExterieur ? (e) => { if (e.target === e.currentTarget) onFermer?.(); } : undefined)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          {sansPanneau ? children : (
            <motion.div
              layoutId={layoutId}
              className={panneauClassName}
              style={panneauStyle}
              initial={anime ? (reduit ? { opacity: 0 } : m.entree) : undefined}
              animate={anime ? (reduit ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }) : undefined}
              exit={reduit ? { opacity: 0 } : m.sortie}
              transition={reduit ? DOUX : { ...RESSORT, exit: SORTIE }}
              /* Le clic dans le panneau ne doit jamais remonter au voile : sans ça, cliquer
                 un champ du formulaire ferme la modale. */
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
